import { SpDropdownToggle } from 'components/shared/Dropdown';
import { Banner } from 'components/shared/ScheduledRun/Blotter/panel/Shared/Banner';
import { OccurrenceReloadModal } from 'components/shared/ScheduledRun/Modal/OccurrenceReloadModal';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import type { IntlKey } from 'locales';
import type { ReactNode } from 'react';
import { Dropdown, DropdownItem, DropdownMenu } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import type { MapStateToProps } from 'react-redux';
import { connect, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import {
  EDITABLE_SCHEDULED_RUN_BLOTTER_ID,
  canReloadScheduledRun,
  getEditableScheduledRun,
  getEditableScheduledRunStatus,
  sendResetRunThunk,
} from 'state/blotters/scheduledQuotes';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import type { ConfirmBannerType } from 'state/scheduledRuns/state';
import { openScheduledRunOccurrencesThunk } from 'state/scheduledRuns/thunks';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { ColorLevel } from 'types/sgBootstrap';
import type { TypedError } from 'utils/errors/errors';
import type { LoadableContent } from 'utils/status';
import { asSuccess } from 'utils/status';

interface DispatchProps {
  onPause: () => void;
  onReschedule: () => void;
  onDelete: () => void;

  onClose: () => void;
  onConfirmBannerChange: (banner?: ConfirmBannerType) => void;
}
interface StateProps {
  showPauseButton: boolean;
  showRescheduleButton: boolean;
  showStoppedReloadMenu: boolean;

  showDeleteButton: boolean;
  bannerType?: ConfirmBannerType;
  updateRun: LoadableContent<undefined, TypedError>;
}

interface Props extends StateProps, DispatchProps {}

const mapStateToProps: MapStateToProps<StateProps, {}, AppState> = state => {
  const status = getEditableScheduledRunStatus(state);
  const editableScheduledRun = getEditableScheduledRun(state);
  return {
    showPauseButton: status === 'Active' || status === 'Error',
    showRescheduleButton: status === 'Paused' || status === 'Error',
    showStoppedReloadMenu: status === 'Stopped',
    showDeleteButton: true,
    bannerType: editableScheduledRun.confirmBanner,
    updateRun: editableScheduledRun.updateRun,
  };
};
const mapDispatchToProps = (dispatch: ThunkDispatch): DispatchProps => ({
  onPause() {
    dispatch(scheduledRunActions.pauseScheduleRequested(EDITABLE_SCHEDULED_RUN_BLOTTER_ID));
  },
  onReschedule() {
    dispatch(scheduledRunActions.rescheduleRequested(EDITABLE_SCHEDULED_RUN_BLOTTER_ID));
  },
  onDelete() {
    dispatch(scheduledRunActions.deleteScheduleRequested(EDITABLE_SCHEDULED_RUN_BLOTTER_ID));
  },
  onConfirmBannerChange(banner) {
    dispatch(scheduledRunActions.setConfirmBanner(EDITABLE_SCHEDULED_RUN_BLOTTER_ID, banner));
  },
  onClose() {
    dispatch(sendResetRunThunk());
  },
});

interface StatusMenuProps {
  colorLevel: ColorLevel;
  analyticAction: 'Suspend' | 'Reschedule';
  bannerType: ConfirmBannerType;

  onBannerChanged: (bannerType: ConfirmBannerType) => void;
  label: IntlKey;

  children: ReactNode;
}
function StatusMenu({ onBannerChanged, bannerType, analyticAction, children, colorLevel, label }: StatusMenuProps) {
  const { formatMessage } = useIntl();

  return (
    <>
      <button
        className={`btn btn-discreet-${colorLevel} btn-lg flex-fill`}
        onClick={() => {
          logAnalytics.trackEvent('Scheduled run', analyticAction);
          onBannerChanged(bannerType);
        }}
      >
        <FormattedMessage id={label} />
      </button>
      <Dropdown>
        <SpDropdownToggle
          aria-label={formatMessage({ id: 'scheduledRun.actionsMenu' })}
          caret={false}
          className={`btn-discreet-${colorLevel} px-3 btn-lg`}
        >
          <em aria-hidden="true" style={{ color: 'inherit' }} className="icon">
            more_vert
          </em>
        </SpDropdownToggle>
        <DropdownMenu>{children}</DropdownMenu>
      </Dropdown>
    </>
  );
}
export const ConnectedStatusFooter = connect(mapStateToProps, mapDispatchToProps)(StatusFooter);
function StatusFooter({
  onConfirmBannerChange,
  onPause,
  onDelete,
  showPauseButton,
  showRescheduleButton,
  showStoppedReloadMenu,
  showDeleteButton,
  bannerType,
  updateRun,
  onClose,
  onReschedule,
}: Props) {
  const { formatMessage } = useIntl();
  const dispatch = useThunkDispatch();
  const isEditing = useSelector((state: AppState) => getEditableScheduledRun(state).type === 'updateQuotes');
  const hasSelectedRun = useSelector(
    (state: AppState) => asSuccess(getEditableScheduledRun(state).runs)?.content.some(run => run.isSelected) ?? false,
  );
  const canReload = useSelector(canReloadScheduledRun);
  const showOccurrenceReload = canReload && !isEditing;
  const occurrenceReloadMenuItem = showOccurrenceReload && (
    <DropdownItem
      className="d-flex justify-content-between"
      disabled={!hasSelectedRun}
      onClick={() => dispatch(openScheduledRunOccurrencesThunk(EDITABLE_SCHEDULED_RUN_BLOTTER_ID))}
    >
      <FormattedMessage id="scheduledRun.occurrenceReload.action" />
      <i className="icon icon-sm ms-2">open_in_new</i>
    </DropdownItem>
  );
  const deleteScheduleMenuItem = (
    <DropdownItem
      className="d-flex justify-content-between btn btn-discreet-danger bg-discreet-danger"
      onClick={() => {
        logAnalytics.trackEvent('Scheduled run', 'Delete');
        onConfirmBannerChange('delete');
      }}
    >
      <FormattedMessage id="scheduledRun.deleteSchedule.button" />
      <i className="icon icon-sm ms-2">cancel</i>
    </DropdownItem>
  );
  return (
    <div className="d-flex flex-fill">
      {!bannerType && (
        <div className="d-flex flex-fill mx-4 mb-4">
          {showPauseButton && (
            <StatusMenu
              analyticAction="Suspend"
              bannerType="pause"
              colorLevel="warning"
              label="scheduledRun.pauseSchedule.button"
              onBannerChanged={onConfirmBannerChange}
            >
              <>
                {occurrenceReloadMenuItem}
                {deleteScheduleMenuItem}
                {showRescheduleButton && (
                  <DropdownItem
                    className="btn btn-primary text-primary"
                    onClick={() => {
                      logAnalytics.trackEvent('Scheduled run', 'Reschedule');
                      onConfirmBannerChange('reschedule');
                    }}
                  >
                    <FormattedMessage id="scheduledRun.reschedule.button" />
                  </DropdownItem>
                )}
              </>
            </StatusMenu>
          )}
          {showRescheduleButton && !showPauseButton && (
            <StatusMenu
              analyticAction="Reschedule"
              bannerType="reschedule"
              colorLevel="secondary"
              label="scheduledRun.reschedule.button"
              onBannerChanged={onConfirmBannerChange}
            >
              {occurrenceReloadMenuItem}
              {deleteScheduleMenuItem}
            </StatusMenu>
          )}
          {!showPauseButton && !showRescheduleButton && showDeleteButton && (
            <>
              <button
                className="btn btn-discreet-danger btn-lg flex-fill"
                onClick={() => onConfirmBannerChange('delete')}
              >
                <FormattedMessage id="scheduledRun.deleteSchedule.button" />
              </button>
              {showOccurrenceReload && showStoppedReloadMenu && (
                <Dropdown drop="up">
                  <SpDropdownToggle
                    aria-label={formatMessage({ id: 'scheduledRun.actionsMenu' })}
                    caret={false}
                    className="btn-discreet-danger px-3 btn-lg"
                  >
                    <em aria-hidden="true" style={{ color: 'inherit' }} className="icon">
                      more_vert
                    </em>
                  </SpDropdownToggle>
                  <DropdownMenu>{occurrenceReloadMenuItem}</DropdownMenu>
                </Dropdown>
              )}
            </>
          )}
        </div>
      )}
      {bannerType === 'reschedule' && (
        <Banner
          onConfirm={onReschedule}
          onDiscard={() => onConfirmBannerChange(undefined)}
          confirmMessageLabel="scheduledRun.reschedule.confirm"
          errorLabel="scheduledRun.reschedule.update.error"
          colorLevel="secondary"
          successMessageLabel="scheduledRun.reschedule.update.success"
          {...updateRun}
          onClose={onClose}
        />
      )}
      {bannerType === 'pause' && (
        <Banner
          onConfirm={onPause}
          onDiscard={() => onConfirmBannerChange(undefined)}
          confirmMessageLabel="scheduledRun.pauseSchedule.confirm"
          errorLabel="scheduledRun.pauseSchedule.update.error"
          colorLevel="warning"
          {...updateRun}
          onClose={onClose}
        />
      )}
      {bannerType === 'delete' && (
        <Banner
          onConfirm={onDelete}
          onDiscard={() => onConfirmBannerChange(undefined)}
          confirmMessageLabel="scheduledRun.deleteSchedule.confirm"
          errorLabel="scheduledRun.deleteSchedule.update.error"
          colorLevel="danger"
          {...updateRun}
          onClose={onClose}
        />
      )}
      {showOccurrenceReload && <OccurrenceReloadModal />}
    </div>
  );
}
